// BRIEF 168 — THE PERSON'S PROSPECT TAG. THE PILL IS GONE FROM HERE.
//
// [RULED Laurent 2026-09-05, at the 168B glance] The tag shows in the CLIENT
// STATUS column instead, as the Prospect pill, and no longer beside the name —
// overturning 168B §1c's "two different marks, two different places". A person
// carrying the tag is not a client of the firm at all, which is a stronger
// statement than how any of their matters stands, so it belongs in the column
// that answers "what is this person to us" rather than as a second mark beside
// their name. `src/lib/clientStandingPill.tsx` owns it now
// (`clientStatusShown`), and the Client-status FILTER reads the same function,
// which is what the same ruling's second half is about.
//
// THIS FILE STAYS, holding what the column did before 168 and does again: the
// name, through `pname`. It is kept rather than folded back into `ecfg.tsx` so
// the `_name` cell has one home and one place to be argued about, and because
// inlining it would move `ecfg.tsx`'s JSX and `any` counters for nothing.
//
// 168's finding about the SHAPE outlived its pill and now applies to the place
// it moved to: a conditional child inside one span changes the serialised
// children of EVERY row (`recordings/57c-entitylist-freeze`), which is why the
// tag was tested first here and why the Client-status cell tests it first too.
//
// ── 168's ORIGINAL HEADER, for the record ─────────────────────────────────
// BRIEF 168 — THE PERSON'S PROSPECT TAG, WHERE A PERSON IS READ.
//
// `Client.isProspect` has been WRITTEN since brief 04 unit 3 (ruling 10 —
// "prospect persons are Client rows carrying the same kind of tag") and
// RENDERED NOWHERE. Censused across `src/` on 2026-09-05: every prospect
// surface in this app is about a MATTER — `matterListCfg`'s status cell,
// `prospectFilter`'s axis and its four money views, `ClientCasesCard`'s badge,
// `OutlookImportModal`'s picker suffix. Not one reads a client's own tag. So
// the flip §1d/§1e perform had no witness, and 168's own glance line — "in the
// Clients list that person's prospect tag is gone" — could not be judged
// either way.
//
// THE PILL IS THE MATTER LIST'S, NOT A NEW ONE: same `.status-prospect` class
// (App.css, `--pp` at 16%), same already-seeded `matters.prospect.badge`, same
// test-the-tag-first shape as `matterListCfg.tsx:597`. No new string, no new
// colour, and NO FILTER — the prospect axis is a matter axis (139 §3) and
// giving persons one is a ruling nobody has asked for.
//
// WHY IT IS A FILE AND NOT A RENDERER INSIDE `ecfg.tsx`. Two reasons, both
// measured. (a) The untagged cell must stay byte-identical:
// `recordings/57c-entitylist-freeze` freezes five client-list screens and none
// of its rows is tagged, so the tag is tested FIRST and the else-branch is the
// span that was already there — a conditional child inside one span changed
// all five. (b) `ecfg.tsx` is `any` at its boundary and every JSX element in it
// costs `react/react-in-jsx-scope`; here the row is typed and React is
// imported, so the pill is added without moving that file's counters at all.
//
// THE LABEL WENT THROUGH `tImperative` because a column render received the
// item and nothing else. That is no longer true — 168B §1b widened the contract
// to `r(it, ctx, rowCtx)` — and it is moot here anyway: this cell renders no
// text of its own now.
import React from "react";
import { pname } from "../utils";

/** What this cell reads off a client row: the display name (through `pname`,
 *  which takes the whole row) and the tag. */
export interface ClientNameRow {
  isProspect?: boolean | null;
  [k: string]: unknown;
}

export function clientNameCell(row: ClientNameRow): React.ReactElement {
  /* The name, and only the name — the pill moved to the Client status column.
     The `title` is 168B GLANCE 4's doing: the cell cap that makes the column
     flexible ellipses the longest name of the 318 on staging, and a name you
     cannot read is not a fix. Same courtesy the Cases cell already gives its
     list of matters. */
  /* This file must stay at ZERO eslint warnings (it is outside
     `eslint-baseline.json`). `pname` always answers with a string, falling
     back to an em dash (`src/utils.ts`), and since 206E §4.3 it is TYPED so —
     `(i: unknown): string` — so its result binds as it is. Until then it was
     `(i: any) => …`, binding it reported `no-unsafe-assignment`, and a
     `String(...)` wrapper kept the count at zero; once typed, that wrapper was
     itself the warning (`no-unnecessary-type-conversion`). */
  const name = pname(row);
  return (
    <span className="name-cell" title={name}>
      {name}
    </span>
  );
}
